/**
 * The landing page's roadmap section ("What we’re building next"): its
 * lists, and which frame each looping preview shows at each step. Copy is
 * the written design's (9 Oct 2026). Tessel Labs' analysis, the chat and
 * the alerts are ILLUSTRATIVE: none of these features exists yet, and no
 * figure here is a claim about the sample deal. The server draws each
 * preview's last frame, so a page with no script or with reduced motion
 * shows the whole thing; the browser starts from step 0 (client/roadmap.ts).
 * The Momentum alarm left the roadmap for a section of its own on 28 Oct
 * 2026 (momentum.ts).
 */

/** One step of every loop: the section's single ticker. */
export const ROADMAP_STEP_MS = 1_000;

// ---------------------------------------------------------------------------
// 01 · AI campaign analysis
// ---------------------------------------------------------------------------

export const ANALYSIS_STEPS = 12;

export const ANALYSIS_SOURCES = [
  'Form C',
  'Pitch page',
  'Financials',
  'Prior rounds',
] as const;

/**
 * One line of the analysis. Its tag is the source it was read from, never a
 * verdict: Raise Sonar describes a raise and doesn't rate it (terms 3.4).
 */
export interface Finding {
  label: string;
  text: string;
  tag: (typeof ANALYSIS_SOURCES)[number];
}

export const FINDINGS: Finding[] = [
  {
    label: 'Valuation',
    text: '$18M cap, above the median of 14 comparable Reg CF SaaS rounds',
    tag: 'Form C',
  },
  {
    label: 'Dilution',
    text: 'Third round in 26 months. Early backers diluted 31%',
    tag: 'Prior rounds',
  },
  {
    label: 'Traction',
    text: 'Revenue up 2.4x year on year in reviewed financials',
    tag: 'Financials',
  },
  {
    label: 'Runway',
    text: 'About 9 months at the current burn rate',
    tag: 'Financials',
  },
];

export type SourceState = 'waiting' | 'reading' | 'read';
export type AnalysisPhase = 'reading' | 'writing' | 'ready';

export const PHASE_LABELS: Record<AnalysisPhase, string> = {
  reading: 'Reading sources',
  writing: 'Writing analysis',
  ready: 'Analysis ready',
};

/** The glyph in front of a source chip, by its state. */
export const SOURCE_GLYPHS: Record<SourceState, string> = {
  waiting: '○',
  reading: '◌',
  read: '✓',
};

export interface AnalysisFrame {
  step: number;
  sources: SourceState[];
  /** How many sources are read: the label's "n OF 4". */
  read: number;
  /** The progress bar's width, 0–1. */
  progress: number;
  /** How many findings, from the top, are revealed. */
  findings: number;
  phase: AnalysisPhase;
  label: string;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/**
 * Step `s` of 12: source i is read once i < s and being read at i = s; the
 * findings come in one a step from step 4; steps 8–11 hold the finished
 * analysis. Any integer is taken modulo the loop.
 */
export function analysisAt(step: number): AnalysisFrame {
  const s = ((step % ANALYSIS_STEPS) + ANALYSIS_STEPS) % ANALYSIS_STEPS;
  const sources = ANALYSIS_SOURCES.map((_, i): SourceState =>
    i < s ? 'read' : i === s ? 'reading' : 'waiting',
  );
  const phase: AnalysisPhase = s < 4 ? 'reading' : s < 8 ? 'writing' : 'ready';
  return {
    step: s,
    sources,
    read: sources.filter((x) => x === 'read').length,
    progress: Math.min(1, s / 7),
    findings: clamp(s - 3, 0, FINDINGS.length),
    phase,
    label: PHASE_LABELS[phase],
  };
}

/** The finished analysis: what the server draws. */
export const ANALYSIS_DONE = analysisAt(ANALYSIS_STEPS - 1);

// ---------------------------------------------------------------------------
// 02 · An AI partner for every campaign
// ---------------------------------------------------------------------------

/** Steps per question and answer; the loop is two of them. */
export const CHAT_PAIR_STEPS = 8;

export const CHAT = [
  {
    q: 'How does Tessel Labs’ valuation compare?',
    a: 'Its $18M cap sits above the median of 14 comparable Reg CF SaaS rounds. Its revenue growth is ahead of most of them too.',
  },
  {
    q: 'What would $1,000 get me?',
    a: 'About 0.0056% of the company at the $18M cap. If the next round dilutes like the last one, that falls to roughly 0.0039%.',
  },
] as const;

export const CHAT_STEPS = CHAT.length * CHAT_PAIR_STEPS;

export interface ChatFrame {
  /** Which question and answer is on screen. */
  pair: number;
  /** The typing indicator is under the question. */
  typing: boolean;
  /** The answer has arrived. */
  answered: boolean;
}

/**
 * Step 0 of a pair asks the question, steps 1–2 show the typing indicator,
 * and steps 3–7 hold the answer. The pair changes at steps 0 and 8.
 */
export function chatAt(step: number): ChatFrame {
  const s = ((step % CHAT_STEPS) + CHAT_STEPS) % CHAT_STEPS;
  const at = s % CHAT_PAIR_STEPS;
  return {
    pair: Math.floor(s / CHAT_PAIR_STEPS),
    typing: at === 1 || at === 2,
    answered: at >= 3,
  };
}

/** The first pair, answered: what the server draws. */
export const CHAT_DONE = chatAt(CHAT_PAIR_STEPS - 1);

// ---------------------------------------------------------------------------
// 04 · SMS, WhatsApp and Telegram alerts
// ---------------------------------------------------------------------------

export const PREVIEW_CHANNELS = ['sms', 'whatsapp', 'telegram'] as const;
export type PreviewChannel = (typeof PREVIEW_CHANNELS)[number];

export const CHANNEL_LABELS: Record<PreviewChannel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
};

/** Steps each channel stays up before the preview moves on by itself. */
export const CHANNEL_STEPS = 3;

/** The channel the preview shows at a step, until a click pins one. */
export function channelAt(step: number): PreviewChannel {
  const n = PREVIEW_CHANNELS.length;
  const i = Math.floor(Math.max(0, step) / CHANNEL_STEPS) % n;
  return PREVIEW_CHANNELS[i];
}

export function previewChannel(value: unknown): PreviewChannel | null {
  return PREVIEW_CHANNELS.find((c) => c === value) ?? null;
}
